import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout from '../../components/TrackerLayout';
import { LEAVE_STATUS_STYLE, fmtLeaveDates, fmtLeaveDays } from '../../components/LeavePanel';
import { useAdminSession } from '../../context/TrackerContext';
import { getLeaveRequests, reviewLeaveRequest } from '../../lib/api';
import type { LeaveRequest, LeaveStatus } from '../../lib/api';

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

const RoleBadge: React.FC<{ r: LeaveRequest }> = ({ r }) => {
  const label = r.employeeType === 'sales' ? 'Sales' : r.role === 'Student Coordinator' ? 'Coordinator' : 'Mentor';
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-md font-semibold"
      style={r.employeeType === 'sales' ? { background: '#dbeafe', color: '#1d4ed8' } : { background: '#ede9fe', color: '#5b21b6' }}>
      {label}
    </span>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
// Leave applied for by mentors, coordinators and salespeople. Only approved
// leave counts — it is filled in as Leave Days on that month's payslip and
// listed on the Attendance page (full-day mentor leave is also marked there).

const LeaveRequests: React.FC = () => {
  const { adminPassword } = useAdminSession();
  const [filter, setFilter] = useState<LeaveStatus | 'All'>('Pending');
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLeaveRequests(adminPassword, filter);
      setRequests(res.requests ?? []);
      setPendingCount(res.pendingCount ?? 0);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword, filter]);

  useEffect(() => { load(); }, [load]);

  const review = async (r: LeaveRequest, decision: 'Approved' | 'Rejected') => {
    setBusy(r._id);
    try {
      const res = await reviewLeaveRequest(adminPassword, r._id, decision, notes[r._id] ?? '');
      toast.success(decision === 'Approved'
        ? `Approved — ${fmtLeaveDays(r.days)} of leave for ${r.name} will show on the payslip and the Attendance page`
        : `Rejected ${r.name}'s leave request`);
      if (!res.emailSentTo) {
        toast.info(`${r.name} has no email saved, so they weren't emailed — add one on the ${r.employeeType === 'sales' ? 'Salespeople' : 'Mentors'} page`);
      }
      load();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">🌴</span>
                <h1 className="text-xl font-bold text-white">Leave Requests</h1>
              </div>
              <p className="text-white/50 text-xs">Approved leave is filled in on that month's payslip and shows on the Attendance page. Each person is emailed your decision.</p>
            </div>
            <button onClick={load}
              className="self-start sm:self-auto px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
              ↻ Refresh
            </button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {(['Pending', 'Approved', 'Rejected', 'All'] as const).map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className="text-xs font-semibold px-4 py-2 rounded-xl border-2 transition-colors"
              style={filter === s
                ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
              {s}{s === 'Pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading requests…</p>
          </div>
        ) : requests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-gray-700 font-bold text-base">{filter === 'Pending' ? 'No leave waiting for approval' : 'Nothing here yet'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map(r => {
              const st = LEAVE_STATUS_STYLE[r.status];
              const isPending = r.status === 'Pending';
              return (
                <div key={r._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  style={isPending ? { borderLeftWidth: 4, borderLeftColor: '#F0B429' } : {}}>
                  <div className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-gray-800">{r.name}</p>
                          <RoleBadge r={r} />
                          <span className="text-xs text-gray-400">({r.employeeId})</span>
                        </div>
                        <p className="text-sm text-gray-700 mt-1">
                          {fmtLeaveDates(r)} · <b className="text-purple-900">{fmtLeaveDays(r.days)}</b>
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">Requested {fmtWhen(r.createdAt)}</p>
                      </div>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-lg" style={{ background: st.bg, color: st.color }}>{r.status}</span>
                    </div>

                    <div className="rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-700">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wide mr-1.5">Reason:</span>{r.reason}
                    </div>

                    {isPending ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input value={notes[r._id] ?? ''} onChange={e => setNotes(n => ({ ...n, [r._id]: e.target.value }))}
                          placeholder={`Note to ${r.name.split(' ')[0]} (optional)`}
                          className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-purple-400" />
                        <div className="flex gap-2">
                          <button onClick={() => review(r, 'Rejected')} disabled={busy === r._id}
                            className="px-4 py-2 rounded-xl text-xs font-bold border-2 disabled:opacity-50"
                            style={{ borderColor: '#ef4444', color: '#b91c1c' }}>
                            ✕ Reject
                          </button>
                          <button onClick={() => review(r, 'Approved')} disabled={busy === r._id}
                            className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50"
                            style={{ background: '#16a34a' }}>
                            {busy === r._id ? '…' : '✓ Approve'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">
                        {r.status} {r.reviewedAt ? `on ${fmtWhen(r.reviewedAt)}` : ''}
                        {r.reviewNote ? <> — <span className="text-gray-600">“{r.reviewNote}”</span></> : ''}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default LeaveRequests;
